import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * <delivery-estimate> — renders "Estimated delivery: <date>" and a
 * "Deliver to <country>" control on the PDP, right above the buy buttons.
 * blocks/delivery-estimate.liquid resolves the *initial* country
 * server-side (safe to cache — same for every visitor from a given
 * country) and emits the full zone config (all three zones, not just
 * whichever one matched); zone resolution, the override, and the date
 * arithmetic itself all happen here, since they depend on client state
 * (the real "now", and whatever the shopper picked) that would go stale or
 * be wrong if baked into a cached page.
 *
 * The order cutoff (e.g. 10:00) is in the warehouse's time zone (block
 * setting; Hong Kong for ROW) for every order regardless of destination —
 * not the visitor's own timezone. "Now" is read as that zone's wall-clock
 * time (Intl, so daylight saving is handled) and stored in a Date's UTC
 * fields; all date arithmetic below uses UTC getters/setters on it, so the
 * visitor's own timezone never leaks in. Dates are formatted in the
 * storefront's language.
 *
 * Stock gating reuses the exact two-part pattern from
 * assets/product-inventory.js: a
 * per-variant availability map embedded in the markup resolves instantly on
 * the synchronous variant:selected event, and a separate data-ready flag
 * (not the availability value itself) gates visibility while a selection
 * can't be confidently resolved yet, so a stale value is never briefly
 * shown as if it were current.
 *
 * The "Deliver to" control is a custom <button> + role="listbox" popover
 * (WAI-ARIA APG's Collapsible Dropdown Listbox pattern), not a native
 * <select> — a native select sizes its box to its WIDEST option, not the
 * current one, which is incompatible with the trigger hugging whatever
 * country name is currently shown. It also has no native <option> for
 * assets/morph.js to reset on variant:update, which is what a native
 * select needed explicit save/restore code to work around. The selected
 * ISO code lives in `data-selected-country-code` on the host element
 * itself (not a descendant), which childrenOnly morphs never touch — no
 * restore logic needed for that value at all; only the listbox's <li>s and
 * the trigger's own text (both descendants, which DO get overwritten by
 * the morph) need re-syncing afterward, via #syncListboxSelection().
 */
class DeliveryEstimate extends HTMLElement {
  static #HONG_KONG_OFFSET_MS = 8 * 60 * 60 * 1000;
  static #DEFAULT_TIME_ZONE = 'Asia/Hong_Kong';
  static #COUNTRY_CHOICE_STORAGE_KEY = 'meermin:delivery-estimate:country-choice';
  static #LEGACY_COUNTRY_OVERRIDE_STORAGE_KEY = 'meermin:delivery-estimate:country-override';
  static #TYPEAHEAD_TIMEOUT_MS = 500;

  /** @type {Record<string, boolean> | undefined} */
  #availabilityMap;

  #typeaheadBuffer = '';

  /** @type {number | undefined} */
  #typeaheadTimeout;

  connectedCallback() {
    this.#resolveStoredCountry();
    this.#renderEstimate();

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onDocumentClick);

    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    this.#closePopup({ restoreFocus: false });
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('click', this.#onDocumentClick);
    clearTimeout(this.#typeaheadTimeout);

    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  #getPickerElements() {
    return {
      trigger: this.querySelector('[data-delivery-estimate-country-trigger]'),
      listbox: this.querySelector('[data-delivery-estimate-country-listbox]'),
    };
  }

  /**
   * Decides whether a stored "Deliver to" choice still applies. The stored
   * record carries the localization.country it was made under, because a
   * change there since (header country/currency selector, redirect modal,
   * fresh geolocation — all full-page /localization round trips) is the
   * shopper's newer, more explicit signal and must win. An override
   * stored forever would otherwise stop the header selector from affecting
   * the estimate once a shopper had touched this control.
   *
   * - Same localization country as when stored: re-apply the stored pick.
   * - Localization country changed: drop the old pick and follow the new
   *   localization country — including over a logged-in customer's
   *   default address, which the server otherwise resolves first; picking
   *   a country in the header is a more recent choice than an address on
   *   file. Persisted as the new pick so that precedence survives reloads.
   * - Nothing stored yet: record the current localization country as the
   *   baseline, so a later header change is detectable at all.
   */
  #resolveStoredCountry() {
    const config = this.#getConfig();
    const localizationCountryCode = config?.localizationCountryCode;
    if (!localizationCountryCode) return;

    const stored = DeliveryEstimate.#getStoredCountryChoice();

    if (stored?.localizationCountryCode === localizationCountryCode) {
      if (stored.countryCode) this.#applyCountry(stored.countryCode);
      return;
    }

    const followsLocalization =
      stored !== null &&
      localizationCountryCode !== config.initialCountryCode &&
      this.#applyCountry(localizationCountryCode);

    DeliveryEstimate.#setStoredCountryChoice({
      countryCode: followsLocalization ? localizationCountryCode : null,
      localizationCountryCode,
    });
  }

  /**
   * Selects the given country if it's one of this listbox's options.
   * @param {string} countryCode
   * @returns {boolean} Whether it was applied.
   */
  #applyCountry(countryCode) {
    const { listbox } = this.#getPickerElements();
    const hasMatch = [...(listbox?.querySelectorAll('[role="option"]') ?? [])].some(
      (option) => option.dataset.value === countryCode
    );
    if (!hasMatch) return false;

    this.dataset.selectedCountryCode = countryCode;
    this.#syncListboxSelection();
    return true;
  }

  /**
   * @returns {{initialCountryCode?: string, localizationCountryCode?: string} | null}
   */
  #getConfig() {
    const script = this.querySelector('[data-delivery-estimate-config]');
    try {
      return script?.textContent ? JSON.parse(script.textContent) : null;
    } catch {
      return null;
    }
  }

  /**
   * @returns {{countryCode: string | null, localizationCountryCode: string} | null}
   */
  static #getStoredCountryChoice() {
    try {
      // Older bare-string override: no localization country to
      // compare against, so it can't be trusted — discard it.
      window.localStorage.removeItem(DeliveryEstimate.#LEGACY_COUNTRY_OVERRIDE_STORAGE_KEY);

      const raw = window.localStorage.getItem(DeliveryEstimate.#COUNTRY_CHOICE_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return typeof parsed?.localizationCountryCode === 'string' ? parsed : null;
    } catch {
      return null;
    }
  }

  /**
   * @param {{countryCode: string | null, localizationCountryCode: string}} choice
   */
  static #setStoredCountryChoice(choice) {
    try {
      window.localStorage.setItem(DeliveryEstimate.#COUNTRY_CHOICE_STORAGE_KEY, JSON.stringify(choice));
    } catch {
      // Private browsing or storage disabled — the override just won't persist across page loads.
    }
  }

  #openPopup() {
    const { trigger, listbox } = this.#getPickerElements();
    if (!trigger || !listbox) return;

    // Unhide but invisible first, so its real (post-layout) height can be
    // measured for the flip decision below without a visible flash at the
    // wrong position — visibility: hidden (unlike the [hidden] attribute)
    // keeps the element in normal layout, just not painted.
    listbox.hidden = false;
    listbox.style.visibility = 'hidden';
    this.#positionListbox(trigger, listbox);
    listbox.style.visibility = '';

    trigger.setAttribute('aria-expanded', 'true');
    window.addEventListener('scroll', this.#onViewportChange, { capture: true, passive: true });
    window.addEventListener('resize', this.#onViewportChange, { passive: true });

    const target =
      listbox.querySelector('[role="option"][aria-selected="true"]') ?? listbox.querySelector('[role="option"]');
    target?.focus();
  }

  /**
   * position: fixed, computed directly from the trigger's own viewport
   * coordinates rather than relying on position: absolute against a
   * position: relative ancestor — see the CSS comment on
   * .delivery-estimate__country-listbox for why (caught live: the listbox
   * rendered positioned against the whole document on at least one real
   * page, ancestor stacking context not fully diagnosed). getBoundingClientRect
   * is already viewport-relative, matching position: fixed's own semantics,
   * so no scroll-offset math is needed.
   *
   * Flips to open upward when there isn't enough room below — caught live:
   * opening straight down from a trigger that sits just above the buy
   * buttons made the ~30-option list overlap Add to cart/Buy with Shop,
   * which looks bad regardless of whether the top/left math is exactly
   * right. This is the standard collision-aware placement real dropdown
   * implementations (Floating UI's "flip", Radix's Popover, Bootstrap's
   * dropdown) all do, not a one-off hack.
   * @param {HTMLElement} trigger
   * @param {HTMLElement} listbox
   */
  #positionListbox(trigger, listbox) {
    const triggerRect = trigger.getBoundingClientRect();
    const listboxHeight = listbox.getBoundingClientRect().height;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const openUpward = spaceBelow < listboxHeight && spaceAbove > spaceBelow;

    listbox.style.left = `${triggerRect.left}px`;

    if (openUpward) {
      listbox.style.top = 'auto';
      listbox.style.bottom = `${window.innerHeight - triggerRect.top + 4}px`;
    } else {
      listbox.style.bottom = 'auto';
      listbox.style.top = `${triggerRect.bottom + 4}px`;
    }
  }

  /**
   * A position: fixed popover doesn't track its trigger during scroll —
   * closing on scroll/resize is simpler and more common than continuously
   * repositioning a rarely-open, occasional-use control. Bound with
   * capture: true specifically to catch scrolling on a nested scrollable
   * ancestor, not just the window/document itself — but that same capture
   * phase also sees the listbox's OWN internal scroll (it's the scrollable
   * element, via overflow-y: auto), which must NOT close it — caught live:
   * without this check, the popup slammed shut the instant a shopper tried
   * to scroll through the country list at all, reading as "not scrollable."
   * @param {Event} event
   */
  #onViewportChange = (event) => {
    const { listbox } = this.#getPickerElements();
    if (event.type === 'scroll' && listbox && event.target instanceof Node && listbox.contains(event.target)) {
      return;
    }
    this.#closePopup({ restoreFocus: false });
  };

  /**
   * @param {{restoreFocus?: boolean}} [options]
   */
  #closePopup({ restoreFocus = false } = {}) {
    const { trigger, listbox } = this.#getPickerElements();
    if (!trigger || !listbox || listbox.hidden) return;

    listbox.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    window.removeEventListener('scroll', this.#onViewportChange, { capture: true });
    window.removeEventListener('resize', this.#onViewportChange);
    if (restoreFocus) trigger.focus();
  }

  /**
   * @param {string} code
   */
  #commitSelection(code) {
    if (!code) return;

    this.dataset.selectedCountryCode = code;
    const localizationCountryCode = this.#getConfig()?.localizationCountryCode;
    if (localizationCountryCode) {
      DeliveryEstimate.#setStoredCountryChoice({ countryCode: code, localizationCountryCode });
    }
    this.#syncListboxSelection();
    this.#closePopup({ restoreFocus: true });
    this.#renderEstimate();

    // One country everywhere: a different "Deliver to"
    // country also changes the store country — currency, prices and the
    // header selector — through the same /localization round trip as the
    // header selector, returning to this page. Options only list countries
    // in localization.available_countries, so every pick is switchable.
    if (localizationCountryCode && code !== localizationCountryCode) this.#changeStoreCountry(code);
  }

  /**
   * @param {string} code - ISO country code.
   */
  #changeStoreCountry(code) {
    const form = this.querySelector('.delivery-estimate__localization-form');
    if (!(form instanceof HTMLFormElement)) return;

    const country = form.querySelector('[name="country_code"]');
    if (country instanceof HTMLInputElement) country.value = code;
    const returnTo = form.querySelector('[name="return_to"]');
    if (returnTo instanceof HTMLInputElement) returnTo.value = window.location.pathname + window.location.search;

    // After the reload the localization country is the new pick, so record
    // the choice against it (it then simply follows the store country).
    DeliveryEstimate.#setStoredCountryChoice({ countryCode: code, localizationCountryCode: code });
    form.submit();
  }

  /**
   * Re-applies the currently selected ISO code onto the listbox's <li>s and
   * the trigger's own text — both are descendants a childrenOnly morph
   * overwrites back to the server-rendered default, unlike
   * data-selected-country-code on the host itself.
   */
  #syncListboxSelection() {
    const code = this.dataset.selectedCountryCode;
    if (!code) return;

    const { listbox } = this.#getPickerElements();
    if (!listbox) return;

    let matched;
    for (const option of listbox.querySelectorAll('[role="option"]')) {
      const isMatch = option.dataset.value === code;
      option.setAttribute('aria-selected', String(isMatch));
      option.tabIndex = isMatch ? 0 : -1;
      if (isMatch) matched = option;
    }

    const triggerText = this.querySelector('[data-delivery-estimate-country-trigger-text]');
    if (matched && triggerText) triggerText.textContent = matched.textContent.trim();
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-delivery-estimate-country-trigger]')) {
      const { listbox } = this.#getPickerElements();
      if (listbox && !listbox.hidden) {
        this.#closePopup({ restoreFocus: false });
      } else {
        this.#openPopup();
      }
      return;
    }

    const option = target.closest('[role="option"]');
    if (option instanceof HTMLElement) this.#commitSelection(option.dataset.value ?? '');
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-delivery-estimate-country-trigger]')) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        this.#openPopup();
      }
      return;
    }

    const focusedOption = target.closest('[role="option"]');
    if (!(focusedOption instanceof HTMLElement)) return;

    const { listbox } = this.#getPickerElements();
    if (!listbox) return;
    const options = [...listbox.querySelectorAll('[role="option"]')];
    const currentIndex = options.indexOf(focusedOption);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        options[Math.min(currentIndex + 1, options.length - 1)]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        options[Math.max(currentIndex - 1, 0)]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        options[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        options[options.length - 1]?.focus();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.#commitSelection(focusedOption.dataset.value ?? '');
        break;
      case 'Escape':
        event.preventDefault();
        this.#closePopup({ restoreFocus: true });
        break;
      case 'Tab':
        this.#closePopup({ restoreFocus: false });
        break;
      default:
        if (event.key.length === 1) this.#handleTypeahead(event.key, options);
    }
  };

  /**
   * Baseline Listbox keyboard support (not the typeahead/autocomplete this
   * component deliberately doesn't need for ~30 items) — jump to the next
   * option starting with the typed character, cycling on repeat presses
   * within a short window. Matches what a native <select> already gives
   * for free, so switching away from one doesn't regress it.
   * @param {string} char
   * @param {HTMLElement[]} options
   */
  #handleTypeahead(char, options) {
    clearTimeout(this.#typeaheadTimeout);
    this.#typeaheadBuffer += char.toLowerCase();
    this.#typeaheadTimeout = window.setTimeout(() => {
      this.#typeaheadBuffer = '';
    }, DeliveryEstimate.#TYPEAHEAD_TIMEOUT_MS);

    const match = options.find((option) => option.textContent?.trim().toLowerCase().startsWith(this.#typeaheadBuffer));
    match?.focus();
  }

  /**
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    const { trigger, listbox } = this.#getPickerElements();
    if (!listbox || listbox.hidden) return;

    const target = event.target instanceof Node ? event.target : null;
    if (target && (trigger?.contains(target) || listbox.contains(target))) return;

    this.#closePopup({ restoreFocus: false });
  };

  /**
   * Parses the per-variant availability map embedded alongside the config,
   * keyed by variant id. Cached until the next variant:update replaces the
   * underlying <script> tag.
   */
  #getAvailabilityMap() {
    if (!this.#availabilityMap) {
      const script = this.querySelector('[data-delivery-estimate-config]');
      try {
        const config = script?.textContent ? JSON.parse(script.textContent) : {};
        this.#availabilityMap = config.availability ?? {};
      } catch {
        this.#availabilityMap = {};
      }
    }
    return this.#availabilityMap;
  }

  /**
   * Fires synchronously on click, well before the section-rendering fetch
   * that #onVariantUpdate waits on resolves. Same instant-resolution
   * pattern as assets/product-inventory.js: only acts when the clicked
   * input resolves to a size option with a known variant id; anything else
   * (a non-size option changing, or a value missing from the map) falls
   * back to the visibility-hidden "not ready" state instead of risking a
   * wrong optimistic value.
   * @param {CustomEvent<{resource: {id: string}}>} event
   */
  #onVariantSelected = (event) => {
    const optionValueId = event.detail?.resource?.id;
    const section = this.closest('.shopify-section, dialog');
    const clickedInput = optionValueId ? section?.querySelector(`[data-option-value-id="${optionValueId}"]`) : null;
    const variantId = clickedInput?.closest('[data-size-option]') ? clickedInput.dataset.variantId : undefined;
    const isAvailable = variantId ? this.#getAvailabilityMap()[variantId] : undefined;

    if (isAvailable === undefined) {
      this.dataset.ready = 'false';
      return;
    }

    this.dataset.available = String(isAvailable);
    this.dataset.ready = 'true';
    this.#renderEstimate();
  };

  /**
   * The confirmed, fetch-resolved update — always the source of truth,
   * whether or not variant:selected already resolved instantly.
   * data-selected-country-code lives on this host element, which a
   * childrenOnly morph never touches, so the shopper's chosen country
   * survives automatically; only the listbox/trigger-text descendants need
   * re-syncing to it afterward.
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const newEstimate = event.detail?.data?.html?.querySelector('delivery-estimate');
    if (!newEstimate) return;

    this.dataset.available = newEstimate.dataset.available;
    morph(this, newEstimate, { childrenOnly: true });
    this.#availabilityMap = undefined;
    this.dataset.ready = 'true';

    this.#syncListboxSelection();
    this.#renderEstimate();
  };

  /**
   * Finds which configured zone (if any) contains the given country code.
   * @param {string} countryCode
   * @param {Record<string, {countries: string[], transitDaysMin: number, transitDaysMax: number, isBusinessDays: boolean}>} zones
   * @returns {{countries: string[], transitDaysMin: number, transitDaysMax: number, isBusinessDays: boolean} | undefined}
   */
  static #resolveZone(countryCode, zones) {
    return Object.values(zones ?? {}).find((zone) => zone.countries?.includes(countryCode));
  }

  #renderEstimate() {
    const labelEl = this.querySelector('[data-delivery-estimate-label]');
    const valueEl = this.querySelector('[data-delivery-estimate-value]');
    const configScript = this.querySelector('[data-delivery-estimate-config]');

    if (!valueEl) return;

    /** @param {string} text */
    const showUnresolved = (text) => {
      if (labelEl) labelEl.hidden = true;
      valueEl.classList.add('delivery-estimate__date-value--muted');
      valueEl.textContent = text;
    };

    if (this.dataset.available === 'false') {
      showUnresolved('');
      return;
    }

    if (!configScript?.textContent) return;

    try {
      const config = JSON.parse(configScript.textContent);
      const activeCountryCode = this.dataset.selectedCountryCode || config.initialCountryCode;
      const zone = DeliveryEstimate.#resolveZone(activeCountryCode, config.zones);

      if (!zone) {
        showUnresolved(valueEl.dataset.fallbackText ?? '');
        return;
      }

      const warehouseNow = DeliveryEstimate.#wallClockNow(config.timeZone || DeliveryEstimate.#DEFAULT_TIME_ZONE);
      const shipDate = DeliveryEstimate.#resolveShipDate(warehouseNow, config.cutoffTime, config.processingDays);
      const formatDate = (date) => DeliveryEstimate.#formatDate(date, config.dateLocale);

      const addDays = zone.isBusinessDays ? DeliveryEstimate.#addBusinessDays : DeliveryEstimate.#addCalendarDays;
      const minDate = addDays(shipDate, Number(zone.transitDaysMin) || 0);
      const maxDate = addDays(shipDate, Number(zone.transitDaysMax) || 0);

      const formattedDate =
        minDate.getTime() === maxDate.getTime()
          ? formatDate(minDate)
          : `${formatDate(minDate)} – ${formatDate(maxDate)}`;

      if (labelEl) labelEl.hidden = false;
      valueEl.classList.remove('delivery-estimate__date-value--muted');
      valueEl.textContent = formattedDate;
    } catch {
      showUnresolved(valueEl.dataset.fallbackText ?? valueEl.textContent ?? '');
    }
  }

  /**
   * The current wall-clock time in `timeZone`, held in a Date's UTC fields
   * (read it back with UTC getters only). Falls back to Hong Kong's fixed
   * +8h if the browser rejects the time zone.
   * @param {string} timeZone - IANA name, e.g. "Asia/Hong_Kong".
   * @returns {Date}
   */
  static #wallClockNow(timeZone) {
    const now = new Date();
    try {
      const parts = {};
      for (const part of new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
      }).formatToParts(now)) {
        if (part.type !== 'literal') parts[part.type] = Number(part.value);
      }
      return new Date(Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour % 24, parts.minute, parts.second));
    } catch {
      return new Date(now.getTime() + DeliveryEstimate.#HONG_KONG_OFFSET_MS);
    }
  }

  /**
   * Finds the next date the order actually ships: today (warehouse time),
   * if the current warehouse time is before the cutoff and today is a
   * processing day; otherwise the next upcoming processing day. Operates
   * entirely via UTC getters/setters on the wall-clock "now" passed in, so
   * no other timezone leaks into the arithmetic.
   * @param {Date} hkNow - Real "now" as warehouse wall-clock time (UTC fields).
   * @param {string} cutoffTime - "HH:MM", 24h, warehouse time.
   * @param {(string | number)[]} processingDays - 0 (Sunday) to 6 (Saturday).
   * @returns {Date}
   */
  static #resolveShipDate(hkNow, cutoffTime, processingDays) {
    const allowedDays = new Set(processingDays.map(Number));
    const [cutoffHours, cutoffMinutes] = (cutoffTime ?? '00:00').split(':').map(Number);
    const isBeforeCutoff =
      hkNow.getUTCHours() < cutoffHours ||
      (hkNow.getUTCHours() === cutoffHours && hkNow.getUTCMinutes() < cutoffMinutes);

    const candidate = new Date(hkNow);
    if (!(isBeforeCutoff && allowedDays.has(candidate.getUTCDay()))) {
      candidate.setUTCDate(candidate.getUTCDate() + 1);
    }

    while (!allowedDays.has(candidate.getUTCDay())) {
      candidate.setUTCDate(candidate.getUTCDate() + 1);
    }

    return candidate;
  }

  /**
   * @param {Date} date - A "Hong Kong now"-shifted date (UTC getters/setters only).
   * @param {number} days
   * @returns {Date}
   */
  static #addCalendarDays(date, days) {
    const result = new Date(date);
    result.setUTCDate(result.getUTCDate() + days);
    return result;
  }

  /**
   * @param {Date} date - A "Hong Kong now"-shifted date (UTC getters/setters only).
   * @param {number} days
   * @returns {Date}
   */
  static #addBusinessDays(date, days) {
    const result = new Date(date);
    let remaining = days;
    while (remaining > 0) {
      result.setUTCDate(result.getUTCDate() + 1);
      const weekday = result.getUTCDay();
      if (weekday !== 0 && weekday !== 6) remaining--;
    }
    return result;
  }

  /**
   * @param {Date} date - A warehouse wall-clock date; formatted via its UTC
   *   fields (which hold the intended calendar date) so the displayed day
   *   doesn't get re-shifted by the viewer's own timezone.
   * @param {string} [locale] - Storefront language (e.g. "en", "fr").
   * @returns {string}
   */
  static #formatDate(date, locale) {
    let resolved = 'en-US';
    try {
      if (locale) resolved = Intl.DateTimeFormat.supportedLocalesOf([locale])[0] || 'en-US';
    } catch {
      resolved = 'en-US';
    }
    return new Intl.DateTimeFormat(resolved, {
      timeZone: 'UTC',
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(date);
  }
}

if (!customElements.get('delivery-estimate')) {
  customElements.define('delivery-estimate', DeliveryEstimate);
}
